import { useSelector, useDispatch } from 'react-redux';
import { useWebSocket } from './useWebSocket';
import { RootState } from '../store/store';
import { useEffect, useRef, useMemo, useCallback } from 'react';
import { VehiclePosition } from '../types/websocket';
import axios from '../api/axios';
import { addVehiclePosition } from '../store/slices/websocketSlice';

/**
 * Vehicle Tracking Hook Configuration
 */
interface VehicleTrackingOptions {
  autoSubscribe?: boolean;
  resubscribeOnReconnect?: boolean;
}

/**
 * Vehicle Tracking Hook
 * 
 * @description
 * Manages vehicle tracking for a specific company via WebSocket channels
 * Automatically subscribes/unsubscribes to company-specific channels
 * Filters vehicle data by company ID
 * 
 * @param {number} companyId - Company ID to track vehicles for
 * @param {VehicleTrackingOptions} [options] - Optional configuration
 * @returns {object} Vehicle tracking data and methods
 * 
 * @example
 * ```tsx
 * const { vehicles, vehicleStatuses, alerts, isConnected } = useVehicleTracking(123);
 * 
 * // vehicles will contain only vehicles for company 123
 * console.log(`Tracking ${vehicles.length} vehicles`);
 * ```
 */
export const useVehicleTracking = (
  companyId: number,
  options: VehicleTrackingOptions = {}
) => {
  const { autoSubscribe = true, resubscribeOnReconnect = true } = options;

  const dispatch = useDispatch();

  // WebSocket connection management
  const { subscribeToChannel, unsubscribeFromChannel, isConnected } = useWebSocket();

  // Redux state selectors - memoized for performance
  const vehiclePositions = useSelector(
    (state: RootState) => state.websocket?.vehiclePositions || {}
  );
  const vehicleStatuses = useSelector(
    (state: RootState) => state.websocket?.vehicleStatuses || {}
  );
  const alerts = useSelector((state: RootState) => state.websocket?.alerts || []);

  // Refs for tracking subscription state
  const hasSubscribedRef = useRef(false);
  const currentCompanyIdRef = useRef(companyId);
  const previousConnectionStateRef = useRef(isConnected);

  // Update company ID reference when it changes
  useEffect(() => {
    currentCompanyIdRef.current = companyId;
  }, [companyId]);

  // Generate channel name for company
  const companyChannel = useMemo(() => `company.${companyId}`, [companyId]);

  /*
   * Hydrate from REST before the first push arrives.
   *
   * This hook used to be a pure WebSocket consumer: empty state on mount, and
   * a vehicle only ever appeared once a push landed. But the backend poller
   * stores one row per provider timestamp and never re-broadcasts a position
   * it has already seen, so on a fleet of OEM-connected cars — which report
   * only while driving — reloading the page meant an empty map for hours while
   * the last known position sat in the database.
   *
   * Deliberately independent of `isConnected`: the snapshot is the useful part
   * even when the socket never comes up. Runs once per companyId; the reducer
   * ignores anything not strictly newer than what it already holds, so a live
   * push that beats this request is never overwritten by it.
   */
  const hydratedForCompanyRef = useRef<number | null>(null);

  useEffect(() => {
    if (!companyId || companyId <= 0) return;
    if (hydratedForCompanyRef.current === companyId) return;
    hydratedForCompanyRef.current = companyId;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await axios.get<{ company_id: number; positions: VehiclePosition[] }>(
          `/fleets/${companyId}/positions/latest`
        );
        if (cancelled || !Array.isArray(data?.positions)) return;

        data.positions.forEach((position) => dispatch(addVehiclePosition(position)));
        console.info(
          '[useVehicleTracking] Hydrated',
          data.positions.length,
          'position(s) from REST'
        );
      } catch (error) {
        // Non-fatal: the live feed remains the primary source. Reset the guard
        // so a companyId change (or a remount) can retry.
        hydratedForCompanyRef.current = null;
        console.warn('[useVehicleTracking] Snapshot hydration failed:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, dispatch]);

  /**
   * Subscribe to company channel
   * @private
   */
  const subscribe = useCallback(() => {
    // Guard: a companyId of 0 means the user has no fleet/company yet.
    // The server would deny 'company.0', so don't even try.
    if (!companyId || companyId <= 0) {
      console.warn('[useVehicleTracking] No valid companyId, skipping subscription');
      return;
    }

    if (!isConnected) {
      console.debug('[useVehicleTracking] Cannot subscribe: not connected');
      return;
    }

    if (hasSubscribedRef.current) {
      console.debug('[useVehicleTracking] Already subscribed to:', companyChannel);
      return;
    }

    console.info('[useVehicleTracking] Subscribing to channel:', companyChannel);
    subscribeToChannel(companyChannel);
    hasSubscribedRef.current = true;
  }, [isConnected, companyChannel, subscribeToChannel]);

  /**
   * Unsubscribe from company channel
   * @private
   */
  const unsubscribe = useCallback(() => {
    if (!hasSubscribedRef.current) {
      console.debug('[useVehicleTracking] Not subscribed to:', companyChannel);
      return;
    }

    console.info('[useVehicleTracking] Unsubscribing from channel:', companyChannel);
    unsubscribeFromChannel(companyChannel);
    hasSubscribedRef.current = false;
  }, [companyChannel, unsubscribeFromChannel]);

  /**
   * Handle channel subscription when connection is established
   */
  useEffect(() => {
    if (!autoSubscribe) return;

    if (isConnected && !hasSubscribedRef.current) {
      subscribe();
    }

    // Cleanup on unmount or when company changes
    return () => {
      if (hasSubscribedRef.current) {
        unsubscribe();
      }
    };
  }, [isConnected, autoSubscribe, subscribe, unsubscribe]);

  /**
   * Handle reconnection scenario
   * Resubscribe if connection is re-established
   */
  useEffect(() => {
    if (!resubscribeOnReconnect) return;

    const wasDisconnected = !previousConnectionStateRef.current;
    const isNowConnected = isConnected;

    // Connection was re-established
    if (wasDisconnected && isNowConnected) {
      console.info('[useVehicleTracking] Reconnected, resubscribing to:', companyChannel);
      hasSubscribedRef.current = false; // Reset flag to allow resubscription
      subscribe();
    }

    previousConnectionStateRef.current = isConnected;
  }, [isConnected, resubscribeOnReconnect, subscribe, companyChannel]);

  /**
   * Reset subscription flag when connection is lost
   */
  useEffect(() => {
    if (!isConnected) {
      hasSubscribedRef.current = false;
    }
  }, [isConnected]);

  /**
   * Filter vehicles for this company
   * Memoized to prevent unnecessary recalculations
   */
  const companyVehicles = useMemo(() => {
    const positions = Object.values(vehiclePositions).filter(
      (position) => (position as VehiclePosition).company_id === companyId
    ) as VehiclePosition[];

    // Sort by timestamp (most recent first)
    return positions.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA;
    });
  }, [vehiclePositions, companyId]);

  /**
   * Filter alerts for this company
   * Memoized to prevent unnecessary recalculations
   */
  interface Alert {
    company_id: number;
    timestamp?: string;
  }

  const companyAlerts = useMemo(() => {
    const filtered = alerts.filter(
      (alert: Alert) => alert.company_id === companyId
    ) as Alert[];

    // Sort by timestamp (most recent first)
    return filtered.sort((a, b) => {
      if (!a.timestamp || !b.timestamp) return 0;
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA;
    });
  }, [alerts, companyId]);

  /**
   * Get active vehicles (vehicles with recent updates)
   * Considers a vehicle active if updated within last 10 minutes
   */
  const activeVehicles = useMemo(() => {
    const tenMinutesAgo = Date.now() - 10 * 60 * 1000;

    return companyVehicles.filter((vehicle) => {
      const lastUpdate = new Date(vehicle.timestamp).getTime();
      return lastUpdate >= tenMinutesAgo;
    });
  }, [companyVehicles]);

  /**
   * Get vehicles by status
   */
  const vehiclesByStatus = useMemo(() => {
    return {
      driving: companyVehicles.filter(
        (v) => v.status === 'driving' || (v.ignition && v.speed > 5)
      ),
      parked: companyVehicles.filter((v) => v.status === 'parked' || (!v.ignition && v.speed === 0)),
      idle: companyVehicles.filter(
        (v) => v.status === 'idle' || (v.ignition && v.speed <= 5)
      ),
      maintenance: companyVehicles.filter((v) => v.status === 'maintenance'),
    };
  }, [companyVehicles]);

  /**
   * Get vehicle by VIN
   */
  const getVehicleByVin = useCallback(
    (vin: string): VehiclePosition | undefined => {
      return companyVehicles.find((v) => v.vin === vin);
    },
    [companyVehicles]
  );

  /**
   * Get statistics about tracked vehicles
   */
  const statistics = useMemo(
    () => ({
      total: companyVehicles.length,
      active: activeVehicles.length,
      inactive: companyVehicles.length - activeVehicles.length,
      driving: vehiclesByStatus.driving.length,
      parked: vehiclesByStatus.parked.length,
      idle: vehiclesByStatus.idle.length,
      maintenance: vehiclesByStatus.maintenance.length,
      totalAlerts: companyAlerts.length,
    }),
    [companyVehicles, activeVehicles, vehiclesByStatus, companyAlerts]
  );

  // Return memoized object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // Data
      vehicles: companyVehicles,
      activeVehicles,
      vehiclesByStatus,
      vehicleStatuses,
      alerts: companyAlerts,
      statistics,

      // Connection state
      isConnected,
      isSubscribed: hasSubscribedRef.current,
      companyId,
      channel: companyChannel,

      // Methods
      subscribe,
      unsubscribe,
      getVehicleByVin,
    }),
    [
      companyVehicles,
      activeVehicles,
      vehiclesByStatus,
      vehicleStatuses,
      companyAlerts,
      statistics,
      isConnected,
      companyId,
      companyChannel,
      subscribe,
      unsubscribe,
      getVehicleByVin,
    ]
  );
};

/**
 * Type exports for TypeScript support
 */
export type UseVehicleTrackingReturn = ReturnType<typeof useVehicleTracking>;
